<template>
  <div v-if="ready" v-show="visible" class="directory-pdf-export" :class="breakpointModifiers">
    <span class="directory-pdf-export__announcement" aria-live="polite">{{ announcement }}</span>

    <sp-modal position="right" :modal-title="t('panel_title')" @update-open="handleDrawerOpenChange">
      <sp-button
        slot="activator"
        variant="appearance-link"
        class="directory-pdf-export__activator"
        :data-testid="TestId.directoryPdf.activator"
      >
        <span slot="prepend" class="directory-pdf-export__activator-icon">
          <sp-progress v-if="generating" type="ellipsis" size="micro" color="primary"></sp-progress>
          <sp-icon v-else-if="complete" name="check-color" size="small"></sp-icon>
          <sp-icon v-else-if="failed" name="important-filled" size="small" fill-color="danger"></sp-icon>
          <sp-icon v-else name="share" size="small" fill-color="primary"></sp-icon>
        </span>
        {{ activatorLabel }}
      </sp-button>

      <ExportReady v-if="complete" />
      <ExportFailed v-else-if="failed" />
      <!-- The form stays mounted through generation, dimmed under the spinner, so
           a validation failure returns to it with scroll position and focus intact. -->
      <ExportForm v-else :total-count="totalCount" :record-cap="recordCap" />

      <ExportActions @submit="handleSubmit" @reset="resetExport" @retry="store.retry" />
    </sp-modal>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import { TestId } from "#/test-ids";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useHostElement } from "#ce/composables/useHostElement";
import { toBoolean, toNumber } from "#ce/utils/props";
import { createDirectoryPdfExportApi, resolveStubOption } from "./api/exports";
import ExportActions from "./components/ExportActions.ce.vue";
import ExportFailed from "./components/ExportFailed.ce.vue";
import ExportForm from "./components/ExportForm.ce.vue";
import ExportReady from "./components/ExportReady.ce.vue";
import { t, whenI18nReady } from "./i18n";
import { props as appProps } from "./props.js";
import { STATUS, DIRECTORY_PDF_EXPORT_KEY, createDirectoryPdfExportStore } from "./store/export";

const props = defineProps(appProps);

const store = createDirectoryPdfExportStore();
provide(DIRECTORY_PDF_EXPORT_KEY, store);
onUnmounted(store.dispose);
const { status, generating, complete, failed, searchChanged } = store;

store.configure(
  createDirectoryPdfExportApi({
    stub: resolveStubOption({
      stubApi: props.stubApi,
      allowStubParam: props.allowStubParam,
      search: window.location.search,
    }),
  }),
);

const ready = ref(false);
const { breakpointModifiers } = useBreakpoints();

// A page can run several finders; this export belongs to the one it sits in.
const host = useHostElement();
const finder = host?.closest("sp-provider-finder") ?? null;

// Pagination, view switches, the sort order, and the entry anchor re-announce
// the same search; the PDF ignores them, so they do not count as a different
// search.
const SIGNATURE_IGNORED_PARAMS = ["page", "per_page", "sort", "view", "visit_type_name", "result_guid"];

const liveTotalCount = ref(null);
const finderSearchQuery = ref(null);
const lastSearchSignature = ref(null);
let submittedSearchSignature = null;

// The finder announces every search with a bubbling `sparkle:search`
// CustomEvent carrying the result count.
useEventListener(finder ?? document, "sparkle:search", handleFinderSearch);

onMounted(async () => {
  adoptCurrentSearch();
  await whenI18nReady;
  ready.value = true;
});

// The finder's first search can run before this element is defined, and the
// event is not replayed, so the finder instance is asked for it on mount.
function adoptCurrentSearch() {
  if (liveTotalCount.value !== null) {
    return;
  }

  const detail = finder?.doctorFinder?.currentSearch();
  if (detail) {
    handleFinderSearch({ detail });
  }
}

function searchSignature(params) {
  const entries = Object.entries(params ?? {})
    .filter(([key]) => !SIGNATURE_IGNORED_PARAMS.includes(key))
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

// Keep generating and completed exports across searches, but do not carry a
// failed attempt into a different search (including browser Back navigation).
function handleFinderSearch({ detail }) {
  if (detail?.source !== "doctor-finder") {
    return;
  }

  const count = Number(detail.resultCount);
  if (Number.isFinite(count)) {
    liveTotalCount.value = count;
  }

  lastSearchSignature.value = searchSignature(detail.params);
  if (typeof detail.url === "string") {
    finderSearchQuery.value = new URL(detail.url, window.location.href).search.replace(/^\?/, "");
  }

  if (submittedSearchSignature !== null) {
    searchChanged.value = lastSearchSignature.value !== submittedSearchSignature;
    if (failed.value && searchChanged.value) {
      resetExport();
    }
  }
}

const totalCount = computed(() => liveTotalCount.value ?? toNumber(props.totalCount) ?? 0);
const recordCap = computed(() => toNumber(props.recordCap) ?? 0);
const visible = computed(() => totalCount.value > 0 || status.value !== STATUS.IDLE);

const activatorLabel = computed(() => {
  if (generating.value) {
    return t("activator_generating_label");
  }
  if (complete.value) {
    return t("activator_complete_label");
  }
  if (failed.value) {
    return t("activator_failed_label");
  }
  return t("activator_label");
});

// The finder deep-links its state, so the page query string is the search; the
// attribute serves mounts whose URL does not carry the search (e.g. embeds).
function currentSearchParams() {
  if (props.searchParams) {
    return props.searchParams;
  }
  if (finder && finderSearchQuery.value !== null) {
    return finderSearchQuery.value;
  }
  return window.location.search.replace(/^\?/, "");
}

function handleSubmit() {
  submittedSearchSignature = lastSearchSignature.value;
  store.submit(currentSearchParams());
}

function resetExport() {
  submittedSearchSignature = null;
  announcement.value = "";
  store.reset();
}

// The panel's own status lives inside the dialog, which is hidden while closed,
// so an outcome that lands then is announced from a live region outside it.
const drawerOpen = ref(false);
const announcement = ref("");

function handleDrawerOpenChange({ detail }) {
  drawerOpen.value = toBoolean(detail?.[0]);
  if (drawerOpen.value) {
    announcement.value = "";
  }
}

watch(status, (next) => {
  if (next === STATUS.FAILED && searchChanged.value) {
    resetExport();
    return;
  }
  if (drawerOpen.value) {
    return;
  }
  if (next === STATUS.COMPLETE) {
    announcement.value = t("complete_title");
  } else if (next === STATUS.FAILED) {
    announcement.value = t("failed_title");
  }
});
</script>

<style lang="scss">
@use "@assets/mixins/visually-hidden" as vh;

:host {
  display: block;
  /* The drawer's title is a heading; render it in the client's heading face. */
  --sp-comp-modal-header-font-family: var(--sp-sys-typescale-h4-font-family);
  /* The mobile sheet holds a steady height across the form/progress/result
     states instead of resizing to each state's content; the card fills it so
     the actions stay pinned to the sheet's bottom edge. */
  --sp-comp-bottom-sheet-content-min-height: 75vh;
  --sp-comp-modal-card-height: 100%;
  --sp-comp-card-padding-block: var(--sp-ref-scale-24);
  --sp-comp-button-border-radius: var(--sp-ref-border-radius-sm);
  --sp-comp-button-font-weight: var(--sp-sys-font-weight-bold);
  --sp-comp-alert-border-radius: var(--sp-ref-border-radius-sm);
  --sp-comp-alert-padding: var(--sp-ref-scale-12);
  --sp-comp-alert-column-gap: var(--sp-ref-scale-12);
}

/* sp-alert spaces a slotted icon from the text, not its own default icon. */
sp-alert[icon]::part(prepend) {
  margin-inline-end: var(--sp-ref-scale-12);
}

/* The sheet's content scrolls under the footer, so the footer marks its edge. */
.directory-pdf-export.--is-sm-screen {
  --sp-comp-card-actions-border-top: 1px solid var(--sp-sys-color-outline-variant);
}

/* sp-button's link variant renders as inline text: the prepend slot stacks
   above the label and its content moves the link on its line. A block with
   a flex row inside keeps one line at a constant height. */
.directory-pdf-export__activator {
  --sp-comp-button-appearance-link-text-decoration: none;
  display: inline-block;
  vertical-align: middle;
  white-space: nowrap;
}

.directory-pdf-export__activator::part(base) {
  display: flex;
  align-items: center;
}

/* Icon-height box, so the shorter ellipsis loader keeps the link's height. */
.directory-pdf-export__activator-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-inline-size: var(--sp-sys-icon-size-small);
  block-size: var(--sp-sys-icon-size-small);
  margin-inline-end: var(--sp-ref-scale-4);
}

.directory-pdf-export__announcement {
  @include vh.visually-hidden;
}

sp-button[slot="actions"] {
  --sp-comp-button-size: var(--sp-ref-scale-56);
  --sp-comp-button-compact-height: var(--sp-ref-scale-56);
  flex: 1 1 0;
  min-width: 0;
}

sp-button[slot="actions"] + sp-button[slot="actions"] {
  margin-inline-start: var(--sp-ref-scale-12);
}
</style>
